import { useMemo } from "react";
import { Card } from "@/design-system/components/ui";
import { usePoolSsns } from "@/hooks/capitalMarkets/useSpvPools";
import type { SpvPoolDto, SsnDto } from "@/types/spvPool";
import { formatCurrency } from "../../shared/format";

interface SsnsTabProps {
  pool: SpvPoolDto;
}

/**
 * Payment System v3.0 PR 6 — Seller Senior Notes (SSNs) issued by this pool.
 * Each row is one SSN: face = 0.95 × segment original principal share.
 * Grouped by seller so the operator can see who holds what.
 */
export function SsnsTab({ pool }: SsnsTabProps) {
  const { data, isLoading } = usePoolSsns(pool.id);
  const rows: SsnDto[] = useMemo(() => data ?? [], [data]);
  const grouped = useMemo(() => groupBySeller(rows), [rows]);

  if (isLoading) {
    return (
      <Card>
        <p className="text-small text-gray-mid italic m-0">Loading SSNs…</p>
      </Card>
    );
  }

  if (rows.length === 0) {
    return (
      <Card>
        <h3 className="font-display font-bold text-h3 m-0">SSNs</h3>
        <p className="text-small text-gray-mid mt-space-2 mb-0">
          No Seller Senior Notes have been issued for this pool yet. SSNs are
          issued automatically when a segment triggers — S1 fires at pool
          formation; S2–S10 fire at investor closings.
        </p>
      </Card>
    );
  }

  const totals = rows.reduce(
    (acc, ssn) => ({
      face: acc.face + ssn.faceAmount,
      balance: acc.balance + ssn.currentBalance,
    }),
    { face: 0, balance: 0 },
  );

  return (
    <div className="flex flex-col gap-space-4">
      <Card>
        <h3 className="font-display font-bold text-h3 m-0">SSNs</h3>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-space-3 mt-space-3">
          <Kpi label="SSNs Issued" value={rows.length.toString()} />
          <Kpi label="Face Total" value={formatCurrency(totals.face)} />
          <Kpi label="Outstanding Total" value={formatCurrency(totals.balance)} />
        </div>
      </Card>

      {grouped.map(([sellerId, sellerRows]) => (
        <Card key={sellerId}>
          <div className="flex items-center justify-between gap-space-2">
            <div>
              <h4 className="font-display font-bold text-onyx m-0">
                Seller {sellerId.slice(0, 8)}…
              </h4>
              <p className="text-small text-gray-mid m-0">
                {sellerRows.length} SSN
                {sellerRows.length === 1 ? "" : "s"} issued
              </p>
            </div>
          </div>
          <table className="w-full mt-space-3 text-small">
            <thead>
              <tr className="text-left text-gray-mid">
                <th className="font-normal py-space-1">SSN</th>
                <th className="font-normal py-space-1">Segment</th>
                <th className="font-normal py-space-1 text-right">Face</th>
                <th className="font-normal py-space-1 text-right">
                  Outstanding
                </th>
                <th className="font-normal py-space-1">Issued</th>
                <th className="font-normal py-space-1">Status</th>
              </tr>
            </thead>
            <tbody>
              {sellerRows.map((s) => (
                <tr key={s.id} className="border-t border-border-light">
                  <td className="py-space-2 font-mono text-[11px]">
                    {s.id.slice(0, 8)}…
                  </td>
                  <td className="py-space-2 font-mono text-[11px]">
                    {s.segmentId.slice(0, 8)}…
                  </td>
                  <td className="py-space-2 text-right">
                    {formatCurrency(s.faceAmount)}
                  </td>
                  <td className="py-space-2 text-right">
                    {formatCurrency(s.currentBalance)}
                  </td>
                  <td className="py-space-2">{s.issueDate}</td>
                  <td className="py-space-2">
                    {s.isRetired ? (
                      <span className="text-gray-mid">Retired</span>
                    ) : (
                      <span className="text-gold-shadow">Outstanding</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      ))}
    </div>
  );
}

function Kpi({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-md border border-border-light p-space-3">
      <div className="text-label uppercase tracking-label text-gray-mid">
        {label}
      </div>
      <div className="text-h4 font-display font-bold text-onyx mt-space-1">
        {value}
      </div>
    </div>
  );
}

function groupBySeller(rows: SsnDto[]): Array<[string, SsnDto[]]> {
  const map = new Map<string, SsnDto[]>();
  for (const row of rows) {
    const list = map.get(row.sellerId) ?? [];
    list.push(row);
    map.set(row.sellerId, list);
  }
  return Array.from(map.entries()).sort(([a], [b]) => a.localeCompare(b));
}
